import { dissoc, pipe, filter, chain, find } from 'ramda';
import PollService from '../services/PollService';
import { normalize, denormalize } from '../services/NormalizeService';
import { defaultErrorHandler } from './';
import { checkLang } from '../services/TranslateService';

const appUrl = new URL(window.location.href);
const definedLang = checkLang(appUrl.searchParams.get('lang'));

const FETCH_POLLS_INTERVAL = 5 * 1000;

const findActiveQuestion = activePolls => {
  return (
    pipe(
      // only keep the sync polls
      filter(poll => !poll.isAsync),
      // get a flattened list of all the questions
      chain(poll => poll.questions),
      // find the one that is active
      find(question => question.isActive || question.isDisplayed)
    )(activePolls) || {}
  );
};

export default {
  state: {
    selectedPollId: -1,
    pollingInterval: null,
    // polls are stored in the following format:
    // {
    //   [pollId]: { ... },
    //   [pollId]: { ... },
    // }
    polls: {},
    inputLanguage: definedLang
  },

  getters: {
    polls: state => denormalize(state.polls),

    asyncPolls: (state, getters) => {
      return getters.polls.filter(poll => poll.isAsync);
    },

    selectedPoll: state => {
      return state.polls[state.selectedPollId] || {};
    },

    getInputLanguage: state => state.inputLanguage
  },

  mutations: {
    SET_POLLING_INTERVAL(state, pollingInterval) {
      state.pollingInterval = pollingInterval;
    },

    SELECT_POLL(state, pollId) {
      state.selectedPollId = pollId;
    },

    SET_POLLS(state, polls) {
      state.polls = polls;
    },

    UPDATE_POLL(state, poll) {
      state.polls[poll.pollId] = poll;
    }
  },

  actions: {
    startPollingPolls({ commit, dispatch }) {
      dispatch('fetchPolls');
      const pollingInterval = setInterval(() => {
        dispatch('fetchPolls');
      }, FETCH_POLLS_INTERVAL);
      commit('SET_POLLING_INTERVAL', pollingInterval);
    },

    fetchPolls({ commit }) {
      PollService.getPolls()
        .then(polls => {
          const activePolls = polls.filter(poll => poll.isActive || poll.isDisplayed);
          const activePollsById = normalize(activePolls, 'pollId');
          commit('SET_POLLS', activePollsById);

          const activeQuestion = findActiveQuestion(activePolls);
          commit('SET_ACTIVE_QUESTION', activeQuestion);
        })
        .catch(defaultErrorHandler);
    },

    selectPoll({ state, dispatch, commit }, pollId) {
      return (state.polls[pollId] ? Promise.resolve() : dispatch('fetchPolls')).then(() => {
        // when fetching the polls, the payload doesn't include the questions,
        // so we need to fetch them here
        return PollService.getPoll(pollId)
          .then(poll => {
            dispatch('addQuestions', poll.questions);
            commit('UPDATE_POLL', dissoc('questions', poll));
            commit('SELECT_POLL', poll.pollId);
          })
          .catch(defaultErrorHandler);
      });
    }
  }
};
